/**
 * ESA Studies sub-components — Phase 2
 *
 * Six dynamic-table editors used inside ElectricalSafetyAuditForm:
 *   - PowerQualityStudy
 *   - ThermographyStudy
 *   - LeakageVoltageStudy
 *   - UnbalancedCurrentStudy
 *   - EarthPitStudy
 *   - AssetMaintenanceStudy
 *
 * Each accepts { value, onChange(newValue) } and stores its data as a plain object/array
 * in the parent form's state.
 */

import React, { useRef } from 'react';
import { Plus, Trash2, Upload, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const getImageSrc = (img) => {
  if (!img) return null;
  if (img.startsWith('data:') || img.startsWith('http')) return img;
  return `${API}${img}`;
};
const fileToBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

// ---------- shared atoms ----------
const Label = ({ children }) => (
  <label className="block text-xs font-medium text-slate-600 mb-1">{children}</label>
);
const Input = (p) => (
  <input
    {...p}
    className={`w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:ring-2 focus:ring-red-300 focus:border-transparent ${p.className || ''}`}
  />
);
const TextArea = (p) => (
  <textarea
    {...p}
    className={`w-full px-2 py-1.5 border border-slate-300 rounded text-xs focus:ring-2 focus:ring-red-300 focus:border-transparent ${p.className || ''}`}
  />
);
const Select = ({ value, onChange, options, ...rest }) => (
  <select
    value={value}
    onChange={onChange}
    {...rest}
    className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs bg-white"
  >
    {options.map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

const SectionHeader = ({ title, subtitle, onAdd, addLabel = 'Add Row' }) => (
  <div className="flex items-center justify-between mb-3">
    <div>
      <h2 className="text-base font-semibold text-slate-800">{title}</h2>
      {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
    {onAdd && (
      <button
        onClick={onAdd}
        data-testid={`add-row-${title.toLowerCase().replace(/\s+/g, '-')}`}
        className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 flex items-center gap-1"
      >
        <Plus size={14} /> {addLabel}
      </button>
    )}
  </div>
);

// ============== POWER QUALITY ==============
export const PowerQualityStudy = ({ value, onChange }) => {
  const v = value || {};
  const params = v.parameters || [];
  const voltageHarmonics = v.voltage_harmonics || [];
  const currentHarmonics = v.current_harmonics || [];

  const update = (k, x) => onChange({ ...v, [k]: x });
  const updateList = (k, idx, patch) =>
    update(k, (v[k] || []).map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  const addRow = (k, row) => update(k, [...(v[k] || []), row]);
  const removeRow = (k, idx) => update(k, (v[k] || []).filter((_, i) => i !== idx));

  return (
    <div className="space-y-6">
      {/* Header info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><Label>Utility Name</Label><Input value={v.utility_name || ''} onChange={(e) => update('utility_name', e.target.value)} placeholder="Incomer" /></div>
        <div><Label>Date of Analysis</Label><Input type="date" value={v.date_of_analysis || ''} onChange={(e) => update('date_of_analysis', e.target.value)} /></div>
        <div><Label>Standard Reference</Label><Input value={v.standard_reference || 'IEEE 519'} onChange={(e) => update('standard_reference', e.target.value)} /></div>
      </div>

      {/* Effects */}
      <div>
        <Label>Effects due to Harmonics</Label>
        <TextArea rows={2} value={v.effects_due_to_harmonics || ''} onChange={(e) => update('effects_due_to_harmonics', e.target.value)} placeholder="Descriptive text explaining impacts" />
      </div>

      {/* Power Quality Monitoring Summary table */}
      <div>
        <SectionHeader
          title="Power Quality Monitoring Summary"
          onAdd={() => addRow('parameters', { parameter: '', uom: '', average: '', minimum: '', maximum: '', remarks: '' })}
          addLabel="Add Parameter"
        />
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-2 py-2 text-left">Parameter</th>
                <th className="px-2 py-2 text-left">UoM</th>
                <th className="px-2 py-2 text-left">Average</th>
                <th className="px-2 py-2 text-left">Minimum</th>
                <th className="px-2 py-2 text-left">Maximum</th>
                <th className="px-2 py-2 text-left">Remarks</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {params.length === 0 && (
                <tr><td colSpan="7" className="px-2 py-4 text-center text-slate-400 italic">No parameters yet</td></tr>
              )}
              {params.map((row, i) => (
                <tr key={i}>
                  <td className="p-1"><Input value={row.parameter || ''} onChange={(e) => updateList('parameters', i, { parameter: e.target.value })} placeholder="Frequency, RMS V, etc" /></td>
                  <td className="p-1"><Input value={row.uom || ''} onChange={(e) => updateList('parameters', i, { uom: e.target.value })} placeholder="Hz, V, A, %" /></td>
                  <td className="p-1"><Input value={row.average || ''} onChange={(e) => updateList('parameters', i, { average: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.minimum || ''} onChange={(e) => updateList('parameters', i, { minimum: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.maximum || ''} onChange={(e) => updateList('parameters', i, { maximum: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.remarks || ''} onChange={(e) => updateList('parameters', i, { remarks: e.target.value })} /></td>
                  <td className="p-1"><button onClick={() => removeRow('parameters', i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Voltage Harmonics */}
      <div>
        <SectionHeader
          title="Voltage Harmonics"
          onAdd={() => addRow('voltage_harmonics', { order: '', percent: '', remarks: 'Normal' })}
          addLabel="Add Order"
        />
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-2 py-2 text-left">Order</th>
                <th className="px-2 py-2 text-left">Voltage %</th>
                <th className="px-2 py-2 text-left">Remarks</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {voltageHarmonics.length === 0 && (
                <tr><td colSpan="4" className="px-2 py-4 text-center text-slate-400 italic">No harmonics added</td></tr>
              )}
              {voltageHarmonics.map((row, i) => (
                <tr key={i}>
                  <td className="p-1"><Input value={row.order || ''} onChange={(e) => updateList('voltage_harmonics', i, { order: e.target.value })} placeholder="H2, H3..." /></td>
                  <td className="p-1"><Input value={row.percent || ''} onChange={(e) => updateList('voltage_harmonics', i, { percent: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.remarks || ''} onChange={(e) => updateList('voltage_harmonics', i, { remarks: e.target.value })} /></td>
                  <td className="p-1"><button onClick={() => removeRow('voltage_harmonics', i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Current Harmonics */}
      <div>
        <SectionHeader
          title="Current Harmonics"
          onAdd={() => addRow('current_harmonics', { order: '', percent: '', remarks: 'Normal' })}
          addLabel="Add Order"
        />
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-2 py-2 text-left">Order</th>
                <th className="px-2 py-2 text-left">Current %</th>
                <th className="px-2 py-2 text-left">Remarks</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentHarmonics.length === 0 && (
                <tr><td colSpan="4" className="px-2 py-4 text-center text-slate-400 italic">No harmonics added</td></tr>
              )}
              {currentHarmonics.map((row, i) => (
                <tr key={i}>
                  <td className="p-1"><Input value={row.order || ''} onChange={(e) => updateList('current_harmonics', i, { order: e.target.value })} placeholder="H2, H3..." /></td>
                  <td className="p-1"><Input value={row.percent || ''} onChange={(e) => updateList('current_harmonics', i, { percent: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.remarks || ''} onChange={(e) => updateList('current_harmonics', i, { remarks: e.target.value })} /></td>
                  <td className="p-1"><button onClick={() => removeRow('current_harmonics', i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== THERMOGRAPHY ==============
const THERMO_PRIORITIES = ['Normal', 'Hotspot', 'Critical Hotspot', 'Very Critical Hotspot'];

export const ThermographyStudy = ({ value, onChange }) => {
  const v = value || {};
  const items = v.items || [];
  const update = (k, x) => onChange({ ...v, [k]: x });
  const updateRow = (idx, patch) =>
    update('items', items.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () =>
    update('items', [...items, { thermal_image_no: '', location: '', panel_name: '', feeder_name: '', hotspot_temp: '', ambient_temp: '', delta_t: '', priority: 'Normal' }]);
  const removeRow = (idx) => update('items', items.filter((_, i) => i !== idx));

  // Auto-summary counts
  const summary = items.reduce(
    (acc, r) => {
      const p = (r.priority || '').toLowerCase();
      if (p === 'normal') acc.normal += 1;
      else if (p === 'hotspot') acc.hotspot += 1;
      else if (p === 'critical hotspot') acc.critical += 1;
      else if (p === 'very critical hotspot') acc.very_critical += 1;
      return acc;
    },
    { normal: 0, hotspot: 0, critical: 0, very_critical: 0 }
  );
  summary.total = items.length;
  summary.action_needed = summary.hotspot + summary.critical + summary.very_critical;

  return (
    <div className="space-y-6">
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
        Thermography summary auto-derived from the items below. For full IR Thermography reports with images,
        use the dedicated IR Thermography module — this section captures a consolidated summary for the ESA.
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        <Card label="Total" value={summary.total} tone="slate" />
        <Card label="Normal" value={summary.normal} tone="emerald" />
        <Card label="Hotspot" value={summary.hotspot} tone="amber" />
        <Card label="Critical" value={summary.critical} tone="orange" />
        <Card label="Very Critical" value={summary.very_critical} tone="rose" />
        <Card label="Action Needed" value={summary.action_needed} tone="red" />
      </div>

      <div>
        <SectionHeader title="Thermography Items" onAdd={addRow} addLabel="Add Item" />
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-2 py-2 text-left">Image No</th>
                <th className="px-2 py-2 text-left">Location</th>
                <th className="px-2 py-2 text-left">Panel</th>
                <th className="px-2 py-2 text-left">Feeder</th>
                <th className="px-2 py-2 text-left">Hotspot °C</th>
                <th className="px-2 py-2 text-left">Ambient °C</th>
                <th className="px-2 py-2 text-left">ΔT</th>
                <th className="px-2 py-2 text-left">Priority</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 && (
                <tr><td colSpan="9" className="px-2 py-4 text-center text-slate-400 italic">No thermography items yet</td></tr>
              )}
              {items.map((row, i) => (
                <tr key={i}>
                  <td className="p-1"><Input value={row.thermal_image_no || ''} onChange={(e) => updateRow(i, { thermal_image_no: e.target.value })} placeholder="IR_000152" /></td>
                  <td className="p-1"><Input value={row.location || ''} onChange={(e) => updateRow(i, { location: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.panel_name || ''} onChange={(e) => updateRow(i, { panel_name: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.feeder_name || ''} onChange={(e) => updateRow(i, { feeder_name: e.target.value })} /></td>
                  <td className="p-1"><Input type="number" value={row.hotspot_temp || ''} onChange={(e) => updateRow(i, { hotspot_temp: e.target.value })} /></td>
                  <td className="p-1"><Input type="number" value={row.ambient_temp || ''} onChange={(e) => updateRow(i, { ambient_temp: e.target.value })} /></td>
                  <td className="p-1"><Input type="number" value={row.delta_t || ''} onChange={(e) => updateRow(i, { delta_t: e.target.value })} /></td>
                  <td className="p-1"><Select value={row.priority || 'Normal'} onChange={(e) => updateRow(i, { priority: e.target.value })} options={THERMO_PRIORITIES} /></td>
                  <td className="p-1"><button onClick={() => removeRow(i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== LEAKAGE VOLTAGE ==============
export const LeakageVoltageStudy = ({ value, onChange }) => {
  const v = value || {};
  const items = v.items || [];
  const update = (k, x) => onChange({ ...v, [k]: x });
  const updateRow = (idx, patch) =>
    update('items', items.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () =>
    update('items', [...items, { location: '', panel_name: '', feeder_name: '', ne_voltage: '', remarks: '' }]);
  const removeRow = (idx) => update('items', items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
        Per IS rules and US best practices, Neutral-to-Earth voltage should remain ≤ 3 V (warehouse) / ≤ 1 V (data center).
      </div>
      <div>
        <Label>Standard Reference</Label>
        <Input value={v.standard_reference || 'N-E voltage ≤ 3 V'} onChange={(e) => update('standard_reference', e.target.value)} />
      </div>
      <SectionHeader title="Leakage Voltage Measurements" onAdd={addRow} />
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left">Location</th>
              <th className="px-2 py-2 text-left">Panel Name</th>
              <th className="px-2 py-2 text-left">Feeder Name</th>
              <th className="px-2 py-2 text-left">N-E Voltage (V)</th>
              <th className="px-2 py-2 text-left">Remarks</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && (
              <tr><td colSpan="6" className="px-2 py-4 text-center text-slate-400 italic">No measurements</td></tr>
            )}
            {items.map((row, i) => {
              const ne = parseFloat(row.ne_voltage);
              const tone = !isNaN(ne) && ne > 3 ? 'bg-rose-50' : '';
              return (
                <tr key={i} className={tone}>
                  <td className="p-1"><Input value={row.location || ''} onChange={(e) => updateRow(i, { location: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.panel_name || ''} onChange={(e) => updateRow(i, { panel_name: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.feeder_name || ''} onChange={(e) => updateRow(i, { feeder_name: e.target.value })} /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={row.ne_voltage || ''} onChange={(e) => updateRow(i, { ne_voltage: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.remarks || ''} onChange={(e) => updateRow(i, { remarks: e.target.value })} placeholder="Satisfactory / High..." /></td>
                  <td className="p-1"><button onClick={() => removeRow(i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============== UNBALANCED CURRENT ==============
export const UnbalancedCurrentStudy = ({ value, onChange }) => {
  const v = value || {};
  const items = v.items || [];
  const update = (k, x) => onChange({ ...v, [k]: x });
  const updateRow = (idx, patch) => {
    const next = items.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    // Auto compute unbalance%
    if (patch.r_amps !== undefined || patch.y_amps !== undefined || patch.b_amps !== undefined) {
      const row = next[idx];
      const r = parseFloat(row.r_amps), y = parseFloat(row.y_amps), b = parseFloat(row.b_amps);
      if (!isNaN(r) && !isNaN(y) && !isNaN(b)) {
        const avg = (r + y + b) / 3;
        if (avg > 0) {
          const maxDev = Math.max(Math.abs(r - avg), Math.abs(y - avg), Math.abs(b - avg));
          row.unbalance_percent = ((maxDev / avg) * 100).toFixed(2);
        }
      }
    }
    update('items', next);
  };
  const addRow = () =>
    update('items', [...items, { location: '', panel_name: '', feeder_name: '', breaker_type: '', breaker_rated_amps: '', cable_size: '', r_amps: '', y_amps: '', b_amps: '', unbalance_percent: '', remarks: '' }]);
  const removeRow = (idx) => update('items', items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
        Standards: 3% voltage unbalance, 10% current unbalance. Unbalance % auto-computed.
      </div>
      <SectionHeader title="Unbalanced Current Measurements" onAdd={addRow} />
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left">Location</th>
              <th className="px-2 py-2 text-left">Panel</th>
              <th className="px-2 py-2 text-left">Feeder</th>
              <th className="px-2 py-2 text-left">Breaker</th>
              <th className="px-2 py-2 text-left">Rated A</th>
              <th className="px-2 py-2 text-left">Cable mm²</th>
              <th className="px-2 py-2 text-left">R (A)</th>
              <th className="px-2 py-2 text-left">Y (A)</th>
              <th className="px-2 py-2 text-left">B (A)</th>
              <th className="px-2 py-2 text-left">Unbal %</th>
              <th className="px-2 py-2 text-left">Remarks</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && (
              <tr><td colSpan="12" className="px-2 py-4 text-center text-slate-400 italic">No measurements</td></tr>
            )}
            {items.map((row, i) => {
              const u = parseFloat(row.unbalance_percent);
              const tone = !isNaN(u) && u > 10 ? 'bg-rose-50' : '';
              return (
                <tr key={i} className={tone}>
                  <td className="p-1"><Input value={row.location || ''} onChange={(e) => updateRow(i, { location: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.panel_name || ''} onChange={(e) => updateRow(i, { panel_name: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.feeder_name || ''} onChange={(e) => updateRow(i, { feeder_name: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.breaker_type || ''} onChange={(e) => updateRow(i, { breaker_type: e.target.value })} placeholder="MCB/MCCB" /></td>
                  <td className="p-1"><Input type="number" value={row.breaker_rated_amps || ''} onChange={(e) => updateRow(i, { breaker_rated_amps: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.cable_size || ''} onChange={(e) => updateRow(i, { cable_size: e.target.value })} /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={row.r_amps || ''} onChange={(e) => updateRow(i, { r_amps: e.target.value })} /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={row.y_amps || ''} onChange={(e) => updateRow(i, { y_amps: e.target.value })} /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={row.b_amps || ''} onChange={(e) => updateRow(i, { b_amps: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.unbalance_percent || ''} readOnly className="bg-slate-50" /></td>
                  <td className="p-1"><Input value={row.remarks || ''} onChange={(e) => updateRow(i, { remarks: e.target.value })} /></td>
                  <td className="p-1"><button onClick={() => removeRow(i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============== EARTH PIT ==============
export const EarthPitStudy = ({ value, onChange }) => {
  const v = value || {};
  const items = v.items || [];
  const update = (k, x) => onChange({ ...v, [k]: x });
  const updateRow = (idx, patch) =>
    update('items', items.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () =>
    update('items', [...items, { location: '', earth_pit_no: '', resistance_ohms: '', remarks: '' }]);
  const removeRow = (idx) => update('items', items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
        Standard: Earth pit resistance ≤ 5 Ω (IS 3043). Rows exceeding threshold highlighted automatically.
      </div>
      <div>
        <Label>Standard Reference</Label>
        <Input value={v.standard_reference || 'IS 3043 (≤ 5 Ω)'} onChange={(e) => update('standard_reference', e.target.value)} />
      </div>
      <SectionHeader title="Earth Pit Measurements" onAdd={addRow} />
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left">Location</th>
              <th className="px-2 py-2 text-left">Pit No</th>
              <th className="px-2 py-2 text-left">Resistance (Ω)</th>
              <th className="px-2 py-2 text-left">Remarks</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && (
              <tr><td colSpan="5" className="px-2 py-4 text-center text-slate-400 italic">No earth pits added</td></tr>
            )}
            {items.map((row, i) => {
              const r = parseFloat(row.resistance_ohms);
              const tone = !isNaN(r) && r > 5 ? 'bg-rose-50' : '';
              return (
                <tr key={i} className={tone}>
                  <td className="p-1"><Input value={row.location || ''} onChange={(e) => updateRow(i, { location: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.earth_pit_no || ''} onChange={(e) => updateRow(i, { earth_pit_no: e.target.value })} placeholder="EP-1" /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={row.resistance_ohms || ''} onChange={(e) => updateRow(i, { resistance_ohms: e.target.value })} /></td>
                  <td className="p-1"><Input value={row.remarks || ''} onChange={(e) => updateRow(i, { remarks: e.target.value })} /></td>
                  <td className="p-1"><button onClick={() => removeRow(i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============== ASSET MAINTENANCE ==============
const DEFAULT_ASSETS = [
  'Transformers', 'LT Panel', 'DG Set', 'UPS Systems', 'Batteries',
  'ACB / MCCB Breakers', 'Bus Ducts', 'Capacitor Bank', 'Earthing System',
  'Cables & Terminations', 'Lightning Arrestor',
];

export const AssetMaintenanceStudy = ({ value, onChange }) => {
  const v = value || {};
  const items = v.items && v.items.length
    ? v.items
    : DEFAULT_ASSETS.map((a) => ({ equipment: a, available: 'No', pm_frequency: '', maintenance_reports: '' }));

  const updateRow = (idx, patch) =>
    onChange({ ...v, items: items.map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  const addRow = () =>
    onChange({ ...v, items: [...items, { equipment: '', available: 'No', pm_frequency: '', maintenance_reports: '' }] });
  const removeRow = (idx) =>
    onChange({ ...v, items: items.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-4">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
        Pre-populated with standard electrical assets. Mark each as Available/Not Available and add PM frequency.
      </div>
      <SectionHeader title="Asset Maintenance Summary" onAdd={addRow} addLabel="Add Asset" />
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left">System / Equipment</th>
              <th className="px-2 py-2 text-left">Available at Site</th>
              <th className="px-2 py-2 text-left">PM Frequency</th>
              <th className="px-2 py-2 text-left">Maintenance Reports</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((row, i) => (
              <tr key={i}>
                <td className="p-1"><Input value={row.equipment || ''} onChange={(e) => updateRow(i, { equipment: e.target.value })} /></td>
                <td className="p-1"><Select value={row.available || 'No'} onChange={(e) => updateRow(i, { available: e.target.value })} options={['Yes', 'No', 'NA']} /></td>
                <td className="p-1"><Input value={row.pm_frequency || ''} onChange={(e) => updateRow(i, { pm_frequency: e.target.value })} placeholder="Annual / Quarterly / Not defined" /></td>
                <td className="p-1"><Input value={row.maintenance_reports || ''} onChange={(e) => updateRow(i, { maintenance_reports: e.target.value })} placeholder="Available / Pending" /></td>
                <td className="p-1"><button onClick={() => removeRow(i)} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============== PHOTO REPORT ==============
export const PhotoReport = ({ value, onChange, findings }) => {
  const items = value || [];
  const fileRefs = useRef({});

  const updateRow = (idx, patch) =>
    onChange(items.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () =>
    onChange([...items, { item_id: '', location: '', observation: '', photo: '' }]);
  const removeRow = (idx) => onChange(items.filter((_, i) => i !== idx));

  const handleFile = async (idx, file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];
    if (!allowed.includes(file.type)) return;
    const base64 = await fileToBase64(file);
    updateRow(idx, { photo: base64 });
  };

  // Aggregated findings photos (read-only display)
  const findingPhotos = (findings || []).flatMap((f, fi) =>
    (f.photos || []).map((p, pi) => ({
      key: `${fi}-${pi}`,
      location: f.location,
      observation: f.observation,
      photo: p,
      priority: f.priority,
    }))
  );

  return (
    <div className="space-y-6">
      {/* Findings photos aggregator */}
      {findingPhotos.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Photos from Findings ({findingPhotos.length})</h3>
          <p className="text-xs text-slate-500 mb-3">Auto-aggregated from the Findings tab. To edit, go to Findings.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {findingPhotos.map((p) => (
              <div key={p.key} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                <img src={getImageSrc(p.photo)} alt={p.location} className="w-full h-28 object-cover" />
                <div className="p-2">
                  <p className="text-xs font-semibold text-slate-700 truncate">{p.location || '—'}</p>
                  <p className="text-[10px] text-slate-500 line-clamp-2">{p.observation}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Additional photos */}
      <div>
        <SectionHeader
          title="Additional Photo Report"
          subtitle="Add extra photos that are not tied to specific findings (e.g. overview shots, site context)."
          onAdd={addRow}
          addLabel="Add Photo"
        />
        {items.length === 0 && (
          <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-lg">
            <p className="text-sm text-slate-500">No extra photos yet</p>
          </div>
        )}
        <div className="space-y-3">
          {items.map((row, i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-3 bg-white">
              <div className="flex items-start gap-3">
                <div className="w-32 h-24 bg-slate-100 rounded border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0 relative group">
                  {row.photo ? (
                    <>
                      <img src={getImageSrc(row.photo)} alt={row.location} className="w-full h-full object-cover" />
                      <button
                        onClick={() => updateRow(i, { photo: '' })}
                        className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={10} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => fileRefs.current[i]?.click()}
                      className="text-xs text-slate-500 hover:text-red-600 flex flex-col items-center gap-1"
                    >
                      <Upload size={20} />
                      Upload
                    </button>
                  )}
                  <input
                    ref={(el) => (fileRefs.current[i] = el)}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(i, f); e.target.value = ''; }}
                  />
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div><Label>Location</Label><Input value={row.location || ''} onChange={(e) => updateRow(i, { location: e.target.value })} /></div>
                  <div className="md:row-span-2">
                    <Label>Observation</Label>
                    <TextArea rows={3} value={row.observation || ''} onChange={(e) => updateRow(i, { observation: e.target.value })} />
                  </div>
                </div>
                <button onClick={() => removeRow(i)} className="p-2 text-rose-500 hover:bg-rose-50 rounded"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============== small UI atoms ==============
const Card = ({ label, value, tone }) => {
  const tones = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`p-2 border rounded text-center ${tones[tone] || tones.slate}`}>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-[10px] uppercase tracking-wider opacity-80">{label}</p>
    </div>
  );
};
